import { Component, ViewChild, ElementRef, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LabelService } from '../services/label.service';
import { ZebraService } from '../services/zebra.service';
import { OrderService } from '../services/order.service';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { Html5Qrcode } from 'html5-qrcode';
import { QrScannerSignalRService } from '../services/qr-scanner-signalr.service';
import { ScannerSessionInfo, ScannerSessionActivation, QrCodeScanResult } from '../models/qr-scanner.models';
import { Subscription } from 'rxjs';
import * as QRCode from 'qrcode';
import { UserPermissionsService } from '../services/user-permissions.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-label-auto-print',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoModule],
  templateUrl: './label-auto-print.component.html',
  styleUrl: './label-auto-print.component.css'
})
export class LabelAutoPrintComponent implements OnInit, OnDestroy {

  @ViewChild('orderIdInput') orderIdInput!: ElementRef;
  @ViewChild('logsContainer') logsContainer!: ElementRef;
  @ViewChild('qrReader') qrReader!: ElementRef;
  orderId: string = '';
  logs: Array<{ error?: boolean, sequential: number, orderId: string, dateTime: string, observation: string, errorCode?: number, lineColor?: string }> = [];
  loading = false;
  printing = false;

  // Variáveis para controle de paginação e infinite scroll
  skip: number = 0;
  limit: number = 20;
  totalLogs: number = 0;
  loadingLogs: boolean = false;
  hasMoreLogs: boolean = true;

  // Variáveis para QR Code Scanner
  qrCodeScanner: Html5Qrcode | null = null;
  isScanning: boolean = false;
  cameraPermissionDenied: boolean = false;
  isProcessingQRCode: boolean = false; // Flag para evitar múltiplas leituras simultâneas
  lastScannedCodes: Map<string, number> = new Map(); // Cache dos últimos QR Codes lidos com timestamp
  readonly SCAN_COOLDOWN_MS = 5000; // Tempo em ms antes de permitir ler o mesmo QR Code novamente (5 segundos)
  currentScannedCode: string | null = null; // QR Code que está sendo processado atualmente

  // Pedido com emissão de nota fiscal em andamento. Enquanto estiver preenchido,
  // as ações das linhas do histórico ficam bloqueadas, para não disparar uma
  // segunda impressão do mesmo pedido antes de a primeira terminar.
  reissuingOrderId: string | null = null;

  // Variáveis para Modo Scanner Mobile (SignalR)
  isMobileScannerModeActive: boolean = false;
  scannerSessionInfo: ScannerSessionInfo | null = null;
  qrCodeImageUrl: string | null = null;
  mobileScannerSubscriptions: Subscription[] = [];
  showQrCodeModal: boolean = false;

  // Contagem regressiva do código de pareamento. A janela é curta de
  // propósito: o QR na tela é um segredo ao portador.
  pairingCodeExpiresAt: Date | null = null;
  pairingSecondsLeft: number = 0;
  private pairingTimer?: ReturnType<typeof setInterval>;

  // Confirmação do pareamento: o operador digita aqui os dígitos que
  // apareceram no celular. Quem fotografou a tela não consegue fazer os
  // dígitos dele chegarem neste campo.
  confirmationInput: string = '';
  confirmingPairing: boolean = false;
  pendingDeviceLabel: string | null = null;

  private readonly SCANNER_SESSION_KEY = 'scanner-station-session';

  todayPrintCount: number | null = null;
  todayPrintCountDate: string | null = null;
  private reconciliationTimeout: any;
  private printCountSub: Subscription | null = null;

  // Controle do carregamento de logs para evitar loop ao destruir o componente
  private loadLogsSub?: Subscription;
  private checkMoreLogsTimeout?: ReturnType<typeof setTimeout>;
  private destroyed = false;

  constructor(
    private labelService: LabelService,
    private zebraService: ZebraService,
    private orderService: OrderService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private qrScannerService: QrScannerSignalRService,
    private permissionsService: UserPermissionsService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.loadLogs();
    this.setupSignalRListeners();

    if (typeof window !== 'undefined') {
      this.restoreScannerSession();
    }

    if (this.permissionsService.hasPermission('labels.view')) {
      this.loadTodayPrintCount();
    }
  }

  /**
   * A sessão da estação vive no `sessionStorage`: sobrevive ao F5 e morre com
   * a aba — exatamente a semântica de "este computador é esta estação".
   */
  private rememberSession(sessionId: string | null): void {
    try {
      if (sessionId) {
        sessionStorage.setItem(this.SCANNER_SESSION_KEY, sessionId);
      } else {
        sessionStorage.removeItem(this.SCANNER_SESSION_KEY);
      }
    } catch {
      // Sem armazenamento (aba privada, SSR): perde só a retomada automática.
    }
  }

  private rememberedSession(): string | null {
    try {
      return sessionStorage.getItem(this.SCANNER_SESSION_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Retoma a sessão que ficou de pé no servidor enquanto este computador
   * esteve fora. O celular continuou pareado o tempo todo — para quem está
   * com ele na mão, um F5 aqui passa despercebido.
   */
  private async restoreScannerSession(): Promise<void> {
    const sessionId = this.rememberedSession();
    if (!sessionId) {
      return;
    }

    try {
      this.scannerSessionInfo = await this.qrScannerService.reattachScannerSession(sessionId);
      this.isMobileScannerModeActive = true;
    } catch {
      // A sessão não existe mais (passou da tolerância, ou foi desativada).
      // O importante é não deixar a tela dizendo "scanner ativo" quando o
      // servidor não tem sessão nenhuma.
      this.rememberSession(null);
      this.isMobileScannerModeActive = false;
      this.scannerSessionInfo = null;
    }

    this.cdr.detectChanges();
  }

  loadTodayPrintCount(): void {
    this.printCountSub = this.labelService.getTodayPrintCount().subscribe({
      next: (res: any) => {
        this.todayPrintCount = res.count;
        this.todayPrintCountDate = res.date;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Erro ao carregar o contador de etiquetas de hoje:', err);
        this.todayPrintCount = null;
        this.cdr.detectChanges();
      }
    });
  }

  private setupSignalRListeners(): void {
    // Listener para quando mobile conecta
    const mobileConnectedSub = this.qrScannerService.onMobileConnected$.subscribe((info: ScannerSessionInfo) => {
      this.scannerSessionInfo = info;
      // Pareou: o código exibido não vale mais e o modal pode fechar.
      this.clearPairingCountdown();
      this.pairingSecondsLeft = 0;
      this.showQrCodeModal = false;
      this.toastr.success(this.transloco.translate('labels.toast.mobileConnected', { name: info.deviceLabel || info.sellerName }), this.transloco.translate('labels.toast.titleScanner'), {
        timeOut: 2000
      });
      this.cdr.detectChanges();
    });

    // Listener para quando mobile desconecta
    const mobileDisconnectedSub = this.qrScannerService.onMobileDisconnected$.subscribe(() => {
      if (this.scannerSessionInfo) {
        this.toastr.warning(this.transloco.translate('labels.toast.mobileDisconnected'), this.transloco.translate('labels.toast.titleScanner'), {
          timeOut: 3000
        });
        // Voltar ao estado inicial ao desconectar
        this.isMobileScannerModeActive = false;
        this.scannerSessionInfo = null;
        this.qrCodeImageUrl = null;
        this.showQrCodeModal = false;
        this.cdr.detectChanges();
      }
    });

    // Listener para quando recebe QR Code do mobile
    const qrCodeScannedSub = this.qrScannerService.onQrCodeScanned$.subscribe((result: QrCodeScanResult) => {
      this.handleQrCodeFromMobile(result.qrCodeData);
    });

    // Listener para quando a sessão é desconectada pelo servidor
    const sessionDisconnectedSub = this.qrScannerService.onSessionDisconnected$.subscribe(() => {
      this.toastr.warning(this.transloco.translate('labels.toast.sessionDisconnected'), this.transloco.translate('labels.toast.titleScanner'), {
        timeOut: 3000
      });
      // Voltar ao estado inicial ao desconectar
      this.isMobileScannerModeActive = false;
      this.scannerSessionInfo = null;
      this.qrCodeImageUrl = null;
      this.showQrCodeModal = false;
      this.cdr.detectChanges();
    });

    // Um aparelho leu o QR e aguarda confirmação. Se o operador não iniciou
    // esse pareamento, é aqui que ele descobre.
    const pairingAttemptSub = this.qrScannerService.onPairingAttempt$.subscribe(info => {
      this.pendingDeviceLabel = info.deviceLabel;
      this.cdr.detectChanges();
    });

    // Reconectou depois de uma queda de rede: retoma a sessão em vez de
    // continuar exibindo um modo scanner que o servidor já não tem.
    const reconnectedSub = this.qrScannerService.onOperatorReconnected$.subscribe(() => {
      this.restoreScannerSession();
    });

    this.mobileScannerSubscriptions.push(mobileConnectedSub, mobileDisconnectedSub, qrCodeScannedSub, sessionDisconnectedSub, pairingAttemptSub, reconnectedSub);
  }

  private handleQrCodeFromMobile(qrCodeData: string): void {
    // Processa como se fosse scanner físico
    if (this.isProcessingQRCode || this.loading || this.printing) {
      return;
    }

    this.orderId = qrCodeData.trim();
    this.isProcessingQRCode = true;

    this.toastr.info(this.transloco.translate('labels.toast.qrReceived', { data: qrCodeData }), this.transloco.translate('labels.toast.titleScanner'), {
      timeOut: 2000
    });

    // Chama print automaticamente
    setTimeout(() => {
      this.print();
    }, 300);
  }

  loadLogs(): void {
    if (this.destroyed || this.loadingLogs || !this.hasMoreLogs) {
      return;
    }

    this.loadingLogs = true;
    this.loadLogsSub = this.labelService.getLogs(this.skip, this.limit)
      .subscribe({
        next: (response) => {
          const items = response.items || [];
          this.totalLogs = response.total || 0;

          // Mapear os logs do backend para o formato do componente
          const mappedLogs = items.map((item: any, index: number) => {
            // Verificar se é erro através do campo type do backend, ou inferir pelo texto se não existir
            const isError = item.type === 'error' ||
              item.observation?.toLowerCase().includes('erro') ||
              item.observation?.toLowerCase().includes('ocorreu');

            // Determinar cor baseado no errorCode ou no tipo
            let lineColor = 'text-green-500';
            if (isError) {
              if (item.errorCode === 21) {
                lineColor = 'text-yellow-500';
              } else {
                lineColor = 'text-red-500';
              }
            }

            return {
              error: isError,
              sequential: this.logs.length + index + 1,
              orderId: item.saleNumber?.toString() || '',
              dateTime: item.dateTime || new Date().toISOString(),
              observation: item.observation || item.action || '',
              errorCode: item.errorCode,
              lineColor: lineColor
            };
          });

          // Se for o primeiro carregamento (skip === 0), adicionar no início (mais recentes primeiro)
          // Se for carregamento subsequente (infinite scroll), adicionar no final (mais antigos)
          if (this.skip === 0) {
            this.logs = [...mappedLogs];
          } else {
            this.logs = [...this.logs, ...mappedLogs];
          }

          // Atualizar controle de paginação.
          // Se a página veio vazia, não há mais o que carregar — sem essa guarda,
          // com total > 0 e itens vazios o skip não avança e o auto-load entra
          // em loop infinito chamando o endpoint de logs.
          this.skip += items.length;
          this.hasMoreLogs = items.length > 0 && this.logs.length < this.totalLogs;
          this.loadingLogs = false;

          // Verificar se precisa carregar mais logs automaticamente (se não há scroll suficiente)
          this.checkMoreLogsTimeout = setTimeout(() => {
            this.checkIfNeedMoreLogs();
          }, 100);
        },
        error: (error) => {
          console.error('Erro ao carregar logs:', error);
          this.loadingLogs = false;
        }
      });
  }

  private checkIfNeedMoreLogs(): void {
    if (this.destroyed || !this.logsContainer || !this.hasMoreLogs || this.loadingLogs) {
      return;
    }

    const container = this.logsContainer.nativeElement;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Só auto-carrega se o container estiver realmente renderizado/visível.
    // Um container destacado ou oculto (ex.: durante a saída da página) tem
    // clientHeight 0, e "scrollHeight (0) <= clientHeight (0)" seria sempre
    // verdadeiro, disparando loadLogs em loop infinito e incrementando o skip
    // até esgotar todas as páginas.
    if (clientHeight > 0 && scrollHeight <= clientHeight && this.hasMoreLogs) {
      this.loadLogs();
    }
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Verificar se chegou próximo ao final (dentro de 50px)
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    if (scrollBottom <= 50 && this.hasMoreLogs && !this.loadingLogs) {
      this.loadLogs();
    }
  }

  private saveLogToBackend(orderId: string, action: string, observation: string, isError: boolean = false, errorCode?: number): void {
    const saleNumber = parseInt(orderId) || 0;
    const dateTime = new Date().toISOString();

    this.labelService.createLog(saleNumber, action, dateTime, observation, isError, errorCode)
      .subscribe({
        next: () => {
          // Log salvo com sucesso
        },
        error: (error) => {
          console.error('Erro ao salvar log no backend:', error);
        }
      });
  }

  // Função chamada ao pressionar Enter
  print(): void {
    // Verificar permissão antes de imprimir
    if (!this.permissionsService.hasPermission('labels.view')) {
      this.toastr.error(this.transloco.translate('labels.toast.noPrintPermission'), this.transloco.translate('labels.toast.titleAccessDenied'));
      return;
    }

    this.spinner.show();
    if (this.orderId) {
      let orderId = this.orderId;
      this.loading = true;
      this.labelService.printByOrder(this.orderId)
        .subscribe({
          next: response => {
            this.loading = false
            if (response != null) {
              this.printing = true;
              this.zebraService.sendToZebraPrinter(response.zebraCode)
                .then((success) => {
                  let logMessage = '';
                  let action = '';
                  if (success) {
                    logMessage = this.transloco.translate('labels.toast.labelSent');
                    action = this.transloco.translate('labels.toast.labelSent');

                    if (this.todayPrintCount !== null) {
                      this.todayPrintCount++;
                      this.cdr.detectChanges();
                    }

                    if (this.reconciliationTimeout) {
                      clearTimeout(this.reconciliationTimeout);
                    }
                    this.reconciliationTimeout = setTimeout(() => {
                      if (!this.destroyed) {
                        this.loadTodayPrintCount();
                      }
                    }, 3000);
                  } else {
                    logMessage = this.transloco.translate('labels.toast.printError');
                    action = this.transloco.translate('labels.toast.printErrorAction');
                  }
                  this.printing = false;

                  const newLog = {
                    error: !success,
                    sequential: this.logs.length + 1,
                    orderId: orderId,
                    dateTime: new Date().toISOString(),
                    observation: logMessage,
                    lineColor: !success ? 'text-red-500' : 'text-green-500'
                  };
                  this.logs.unshift(newLog);

                  // Persistir log no backend
                  this.saveLogToBackend(orderId, action, logMessage, !success);

                  // Limpar campo e reativar scanner se estiver ativo
                  this.orderId = '';
                  this.isProcessingQRCode = false;
                  this.currentScannedCode = null; // Liberar o QR Code atual

                  // Se o scanner estava ativo, manter ativo para próxima leitura
                  if (this.isScanning && this.qrCodeScanner) {
                    // Scanner já está ativo, apenas limpar o campo
                  } else {
                    this.focusInputField();
                  }
                });
              this.spinner.hide();
            }
            else {
              const observation = response.description ?? this.transloco.translate('labels.toast.labelFetchError');
              this.logs.unshift({
                error: true,
                sequential: this.logs.length + 1,
                orderId: orderId,
                dateTime: new Date().toISOString(),
                lineColor: 'text-red-500',
                observation: observation
              });

              // Persistir log no backend
              this.saveLogToBackend(orderId, this.transloco.translate('labels.toast.labelFetchErrorAction'), observation, true);

              // Limpar campo e reativar scanner se estiver ativo
              this.orderId = '';
              this.isProcessingQRCode = false;
              this.currentScannedCode = null; // Liberar o QR Code atual

              // Se o scanner estava ativo, manter ativo para próxima leitura
              if (!this.isScanning || !this.qrCodeScanner) {
                this.focusInputField();
              }

              this.spinner.hide();
            }
          },
          error: response => {
            const observation = response.error?.description ?? this.transloco.translate('labels.toast.labelFetchError');
            const action = response.error?.error_code == 21 ? this.transloco.translate('labels.toast.invoiceNotIssued') : this.transloco.translate('labels.toast.labelFetchErrorAction');

            this.logs.unshift({
              error: true,
              sequential: this.logs.length + 1,
              orderId: orderId,
              dateTime: new Date().toISOString(),
              lineColor: response.error?.error_code == 21 ? 'text-yellow-500' : 'text-red-500',
              errorCode: response.error?.error_code,
              observation: observation
            });

            // Persistir log no backend
            this.saveLogToBackend(orderId, action, observation, true, response.error?.error_code);

            // Limpar campo e reativar scanner se estiver ativo
            this.orderId = '';
            this.isProcessingQRCode = false;
            this.currentScannedCode = null; // Liberar o QR Code atual

            // Se o scanner estava ativo, manter ativo para próxima leitura
            if (!this.isScanning || !this.qrCodeScanner) {
              this.focusInputField();
            }

            this.loading = false;
            this.spinner.hide();
          }
        })
    }
  }

  // Enquanto uma impressão está em andamento, as ações das linhas do histórico
  // (emitir nota fiscal, reimprimir) ficam desabilitadas.
  get rowActionsDisabled(): boolean {
    return this.loading || this.printing || this.reissuingOrderId !== null;
  }

  // Reemite a etiqueta de uma linha que falhou, sem o operador precisar
  // localizar o pedido e bipar de novo. A tentativa entra no histórico como
  // qualquer outra impressão.
  reprint(orderId: string): void {
    if (!orderId || this.rowActionsDisabled) {
      return;
    }

    this.orderId = orderId;
    this.print();
  }

  invoiceOrder(orderId: string, sequential: number): void {
    if (this.rowActionsDisabled) {
      return;
    }

    this.reissuingOrderId = orderId;
    this.spinner.show();
    this.orderService.invoiceAndGetOrder(orderId)
      .subscribe({
        next: response => {
          let log = this.logs.find(x => x.sequential == sequential);
          if (log) {
            log.errorCode = 0;
            log.observation = this.transloco.translate('labels.toast.invoiceIssuedWaiting');
            log.lineColor = 'text-gray-500';
          }
          setTimeout(() => {
            this.reissuingOrderId = null;
            this.orderId = orderId;
            this.isProcessingQRCode = true;
            this.print();

          }, 6000);
        },
        error: error => {
          let log = this.logs.find(x => x.sequential == sequential);
          if (log) {
            log.errorCode = 0;
            log.observation = this.transloco.translate('labels.toast.invoiceIssueError');
          }
          // Fora do `if (log)`: sem isso, quando a linha não é encontrada o
          // spinner fica girando para sempre e a tela trava.
          this.reissuingOrderId = null;
          this.spinner.hide();
        }
      })
  }

  focusInputField(): void {
    setTimeout(() => {
      this.orderIdInput.nativeElement.focus();
    }, 0);
  }

  // Métodos para QR Code Scanner
  async startQRCodeScanner(): Promise<void> {
    if (this.isScanning) {
      return;
    }

    try {
      this.isScanning = true;
      this.cameraPermissionDenied = false;

      // Forçar detecção de mudanças para renderizar o elemento
      this.cdr.detectChanges();

      // Aguardar o Angular renderizar o elemento no DOM
      await new Promise(resolve => setTimeout(resolve, 200));

      const qrCodeId = 'qr-reader';
      let element = document.getElementById(qrCodeId);

      // Tentar novamente se não encontrou
      if (!element) {
        await new Promise(resolve => setTimeout(resolve, 200));
        element = document.getElementById(qrCodeId);
      }

      if (!element) {
        throw new Error('Elemento qr-reader não encontrado no DOM');
      }

      this.qrCodeScanner = new Html5Qrcode(qrCodeId);

      const config = {
        fps: 10,
        qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
          const minEdgePercentage = 0.7; // 70% da menor dimensão
          const minEdgeSize = Math.min(viewfinderWidth, viewfinderHeight);
          const qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);
          return {
            width: qrboxSize,
            height: qrboxSize
          };
        },
        aspectRatio: 1.0
      };

      await this.qrCodeScanner.start(
        { facingMode: 'environment' }, // Usar câmera traseira
        config,
        (decodedText: string) => {
          // QR Code detectado
          this.onQRCodeScanned(decodedText);
        },
        (errorMessage: string) => {
          // Ignorar erros de leitura (são normais enquanto não detecta QR code)
        }
      );
    } catch (error: any) {
      console.error('Erro ao iniciar scanner QR Code:', error);
      this.isScanning = false;

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        this.cameraPermissionDenied = true;
      }
    }
  }

  stopQRCodeScanner(): void {
    if (this.qrCodeScanner) {
      this.qrCodeScanner.stop().then(() => {
        this.qrCodeScanner?.clear();
        this.qrCodeScanner = null;
        this.isScanning = false;
      }).catch((error) => {
        console.error('Erro ao parar scanner:', error);
        this.isScanning = false;
      });
    }
  }

  toggleQRCodeScanner(): void {
    if (this.isScanning) {
      this.stopQRCodeScanner();
    } else {
      this.startQRCodeScanner();
    }
  }

  onQRCodeScanned(decodedText: string): void {
    // Evitar múltiplas leituras simultâneas
    if (this.isProcessingQRCode || this.loading || this.printing) {
      return;
    }

    const scannedOrderId = decodedText.trim();

    // Se é o mesmo QR Code que está sendo processado, ignorar completamente
    if (this.currentScannedCode === scannedOrderId) {
      return;
    }

    const now = Date.now();

    // Verificar se este QR Code já foi lido recentemente
    const lastScanTime = this.lastScannedCodes.get(scannedOrderId);
    if (lastScanTime && (now - lastScanTime) < this.SCAN_COOLDOWN_MS) {
      // QR Code já foi lido recentemente, ignorar
      return;
    }

    // Marcar este QR Code como sendo processado
    this.currentScannedCode = scannedOrderId;

    // Registrar este QR Code como lido agora
    this.lastScannedCodes.set(scannedOrderId, now);

    // Limpar QR Codes antigos do cache (mais de 30 segundos)
    const thirtySecondsAgo = now - 30000;
    for (const [code, timestamp] of this.lastScannedCodes.entries()) {
      if (timestamp < thirtySecondsAgo) {
        this.lastScannedCodes.delete(code);
      }
    }

    // Emitir beep
    this.playBeep();

    // Preencher o campo e processar
    this.orderId = scannedOrderId;
    this.isProcessingQRCode = true;

    // Mostrar feedback visual
    this.toastr.success(this.transloco.translate('labels.toast.qrDetected', { id: scannedOrderId }), this.transloco.translate('labels.toast.titleSuccess'), {
      timeOut: 2000
    });

    // Chamar print automaticamente após um pequeno delay
    setTimeout(() => {
      this.print();
    }, 500);
  }

  playBeep(): void {
    try {
      // Criar um contexto de áudio para o beep
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Frequência do beep (Hz)
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      // Se não conseguir criar o beep, apenas ignora (pode não ter suporte em alguns navegadores)
      console.warn('Não foi possível reproduzir o beep:', error);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearPairingCountdown();

    if (this.reconciliationTimeout) {
      clearTimeout(this.reconciliationTimeout);
    }
    if (this.printCountSub) {
      this.printCountSub.unsubscribe();
    }

    // Cancelar carregamento de logs em andamento e o auto-load pendente. Sem
    // isso, o ciclo loadLogs -> checkIfNeedMoreLogs continua rodando em um
    // componente já destruído (container destacado tem scrollHeight 0),
    // gerando um loop infinito de chamadas ao endpoint de logs.
    this.loadLogsSub?.unsubscribe();
    if (this.checkMoreLogsTimeout) {
      clearTimeout(this.checkMoreLogsTimeout);
    }

    // Garantir que o scanner seja parado ao destruir o componente
    if (this.isScanning) {
      this.stopQRCodeScanner();
    }

    // Desativar modo scanner mobile se estiver ativo
    if (this.isMobileScannerModeActive) {
      this.deactivateMobileScannerMode();
    }

    // Limpar subscriptions
    this.mobileScannerSubscriptions.forEach(sub => sub.unsubscribe());
  }

  // Métodos para Modo Scanner Mobile
  async activateMobileScannerMode(): Promise<void> {
    try {
      this.spinner.show();
      const activation = await this.qrScannerService.activateScannerMode();
      this.scannerSessionInfo = activation.session;
      this.isMobileScannerModeActive = true;
      this.rememberSession(activation.session.sessionId);

      await this.showPairingCode(activation);
      this.showQrCodeModal = true;

      this.toastr.success(this.transloco.translate('labels.toast.scannerModeOn'), this.transloco.translate('labels.toast.titleScanner'), {
        timeOut: 2000
      });
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Erro ao ativar modo scanner mobile:', error);
      this.toastr.error(this.transloco.translate('labels.toast.scannerModeOnError'), this.transloco.translate('labels.toast.titleError'), {
        timeOut: 3000
      });
    } finally {
      this.spinner.hide();
    }
  }

  /** Emite um novo código; o anterior deixa de valer na hora. */
  async regeneratePairingCode(): Promise<void> {
    try {
      this.spinner.show();
      const activation = await this.qrScannerService.regeneratePairingCode();
      this.scannerSessionInfo = activation.session;
      await this.showPairingCode(activation);
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Erro ao gerar novo código de pareamento:', error);
      this.toastr.error(this.transloco.translate('labels.toast.pairingCodeError'), this.transloco.translate('labels.toast.titleError'), {
        timeOut: 3000
      });
    } finally {
      this.spinner.hide();
    }
  }

  /** Desconecta o celular pareado sem encerrar a sessão. */
  async revokePairedDevice(): Promise<void> {
    try {
      await this.qrScannerService.revokePairedDevice();
      if (this.scannerSessionInfo) {
        this.scannerSessionInfo.isConnected = false;
        this.scannerSessionInfo.deviceLabel = undefined;
      }
      this.toastr.info(this.transloco.translate('labels.toast.deviceRevoked'), this.transloco.translate('labels.toast.titleScanner'), {
        timeOut: 2500
      });
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Erro ao desconectar o celular:', error);
    }
  }

  /**
   * Confirma o pareamento. Só aqui o aparelho vira scanner — e as outras
   * tentativas pendentes, incluindo a de quem tenha fotografado a tela,
   * são descartadas.
   */
  async confirmPairing(): Promise<void> {
    const codigo = (this.confirmationInput || '').trim();
    if (!codigo || this.confirmingPairing) {
      return;
    }

    this.confirmingPairing = true;
    try {
      this.scannerSessionInfo = await this.qrScannerService.confirmScannerPairing(codigo);
      this.confirmationInput = '';
      this.pendingDeviceLabel = null;
      this.clearPairingCountdown();
      this.pairingSecondsLeft = 0;
      this.showQrCodeModal = false;

      this.toastr.success(this.transloco.translate('labels.toast.pairingConfirmed'), this.transloco.translate('labels.toast.titleScanner'), {
        timeOut: 2500
      });
    } catch (error: any) {
      this.toastr.error(
        error?.message || this.transloco.translate('labels.toast.pairingConfirmError'),
        this.transloco.translate('labels.toast.titleError'),
        { timeOut: 5000 });
    } finally {
      this.confirmingPairing = false;
      this.cdr.detectChanges();
    }
  }

  private async showPairingCode(activation: ScannerSessionActivation): Promise<void> {
    this.confirmationInput = '';
    this.pendingDeviceLabel = null;
    this.pairingCodeExpiresAt = new Date(activation.pairingCodeExpiresAt);

    // O código vai no fragmento da URL. Fragmento não é enviado ao servidor:
    // não entra em log de acesso nem no cabeçalho Referer.
    const url = `${window.location.origin}/scanner#c=${activation.pairingCode}`;
    await this.generateQRCode(url);

    this.startPairingCountdown();
  }

  private startPairingCountdown(): void {
    this.clearPairingCountdown();
    this.updatePairingSecondsLeft();

    this.pairingTimer = setInterval(() => {
      this.updatePairingSecondsLeft();
      if (this.pairingSecondsLeft <= 0) {
        this.clearPairingCountdown();
      }
      this.cdr.detectChanges();
    }, 1000);
  }

  private updatePairingSecondsLeft(): void {
    if (!this.pairingCodeExpiresAt) {
      this.pairingSecondsLeft = 0;
      return;
    }
    const remaining = (this.pairingCodeExpiresAt.getTime() - Date.now()) / 1000;
    this.pairingSecondsLeft = Math.max(0, Math.round(remaining));
  }

  private clearPairingCountdown(): void {
    if (this.pairingTimer) {
      clearInterval(this.pairingTimer);
      this.pairingTimer = undefined;
    }
  }

  async deactivateMobileScannerMode(): Promise<void> {
    try {
      this.spinner.show();
      await this.qrScannerService.deactivateScannerMode();
      this.clearPairingCountdown();
      this.rememberSession(null);
      this.isMobileScannerModeActive = false;
      this.scannerSessionInfo = null;
      this.qrCodeImageUrl = null;
      this.pairingCodeExpiresAt = null;
      this.pairingSecondsLeft = 0;
      this.showQrCodeModal = false;

      this.toastr.info(this.transloco.translate('labels.toast.scannerModeOff'), this.transloco.translate('labels.toast.titleScanner'), {
        timeOut: 2000
      });
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Erro ao desativar modo scanner mobile:', error);
      this.toastr.error(this.transloco.translate('labels.toast.scannerModeOffError'), this.transloco.translate('labels.toast.titleError'), {
        timeOut: 3000
      });
    } finally {
      this.spinner.hide();
    }
  }

  closeQrCodeModal(): void {
    this.showQrCodeModal = false;

    // Se fechar a modal sem conectar, desativar o modo scanner e voltar ao estado inicial
    if (this.isMobileScannerModeActive && !this.scannerSessionInfo?.isConnected) {
      this.deactivateMobileScannerMode();
    }
  }

  private async generateQRCode(sessionId: string): Promise<void> {
    try {
      this.qrCodeImageUrl = await QRCode.toDataURL(sessionId, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error);
      this.toastr.error(this.transloco.translate('labels.toast.qrGenError'), this.transloco.translate('labels.toast.titleError'), {
        timeOut: 3000
      });
    }
  }
}
